import type { DeviceSync } from "@workspace/api-client-react";
import type { QueueEntry } from "@workspace/player-core";
import { devLogStats } from "@/lib/devlog-boot";
import {
  getPlayerDebugBuffer,
  isPlayerDebugEnabled,
  type AudioElementSnapshot,
} from "@/player/lib/player-debug";

type Props = {
  sync: DeviceSync | null;
  current: QueueEntry | null;
  isPlaying: boolean;
  scheduleEnabled: boolean;
  audioSnapshot: AudioElementSnapshot | null;
  audioContextState: string | null;
  stallWarning: string | null;
};

function slotSummary(sync: DeviceSync | null): string {
  if (!sync?.plan?.channels?.length) return "no plan channels";
  return sync.plan.channels
    .map((ch) => {
      const n = ch.now;
      if (!n || n.source === "none") return `${ch.channelType}: idle`;
      return `${ch.channelType}: ${n.scheduleName ?? n.playlistName ?? n.source}`;
    })
    .join(" | ");
}

export function PlayerDebugPanel({
  sync,
  current,
  isPlaying,
  scheduleEnabled,
  audioSnapshot,
  audioContextState,
  stallWarning,
}: Props) {
  if (!isPlayerDebugEnabled()) return null;

  const recent = getPlayerDebugBuffer().slice(-8).reverse();
  const devlogStats = devLogStats();

  return (
    <div
      data-testid="player-debug-panel"
      className="fixed bottom-0 left-0 right-0 z-50 max-h-[45vh] overflow-auto border-t bg-background/95 p-3 text-xs font-mono shadow-lg backdrop-blur-sm"
    >
      <div className="mb-2 font-semibold text-foreground">Player debug</div>
      <div className="grid gap-1 text-muted-foreground">
        <div>
          <span className="text-foreground">sync:</span>{" "}
          {sync?.syncVersion ?? "—"}
        </div>
        <div>
          <span className="text-foreground">plan:</span> {slotSummary(sync)}
        </div>
        <div>
          <span className="text-foreground">now:</span>{" "}
          {current
            ? `${current.channelType} · ${current.item.title ?? "?"} · ${isPlaying ? "playing" : "paused"}`
            : "idle"}
        </div>
        <div>
          <span className="text-foreground">schedule:</span>{" "}
          {scheduleEnabled ? "on" : "off"}
        </div>
        {/*
          DEVLOGS-TEMP: delivery counters for the dev debug-log pipeline.
          "No logs" and "no events" look identical from the query side, and a
          blocked endpoint is invisible because the shipper is deliberately
          fire-and-forget. This line makes the difference visible at a glance.
          Null whenever the pipeline is unconfigured, i.e. every production
          build. Additive only — the panel's testid and its
          isPlayerDebugEnabled() gate are unchanged, so the e2e specs still hold.
        */}
        {devlogStats ? (
          <div>
            <span className="text-foreground">devlog:</span>{" "}
            {`sent=${devlogStats.sent} failed=${devlogStats.failed} dropped=${devlogStats.dropped} queued=${devlogStats.queued}`}
            {devlogStats.failed > 0 && devlogStats.sent === 0 ? (
              <span className="text-amber-600 dark:text-amber-400">
                {" "}
                — reaching nothing; check VITE_DEVLOG_URL / CORS
              </span>
            ) : null}
          </div>
        ) : null}
        <div>
          <span className="text-foreground">audio ctx:</span>{" "}
          {audioContextState ?? "—"}
          {audioSnapshot
            ? ` · ${audioSnapshot.paused ? "paused" : "playing"} · readyState=${audioSnapshot.readyState}`
            : ""}
        </div>
        {stallWarning ? (
          <div className="text-amber-600 dark:text-amber-400">
            stall: {stallWarning}
          </div>
        ) : null}
      </div>
      {recent.length > 0 ? (
        <div className="mt-2 border-t pt-2">
          <div className="mb-1 font-semibold text-foreground">Recent events</div>
          <ul className="space-y-0.5">
            {recent.map((e, i) => (
              <li key={`${e.ts}-${i}`} className="truncate text-muted-foreground">
                <span className="text-foreground">{e.category}</span>{" "}
                {Object.keys(e.data).length > 0
                  ? JSON.stringify(e.data)
                  : ""}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
